import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';

import asFormField from './asFormField';
import { clone, debounce } from 'lodash';

export const NumberInput = ({
    name,
    value,
    onChange,
    onBlur,
    className,
    style,

    placeholder,
    disabled,
    min,
    max,
    step,
    onlyIntegers,
    prefixAddon,
    debounce: enableDebounce,
    ...otherProps
}) => {
    // Keep internal state to debounce & optimize rerenders
    const [internalValue, setInternalValue] = useState(value);
    // If external state updated, then update internal state
    useEffect(() => setInternalValue(value), [value]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnChange = useCallback(debounce(onChange, 200), [onChange]);

    const handleChange = (e) => {
        const newValue = e.target.value;
        setInternalValue(newValue);
        const changeFn = enableDebounce ? debouncedOnChange : onChange;
        changeFn(clone(e));
    };

    const handleKeyDown = (event) => {
        const blockKeys = ['e', '+', ...(min != null && min >= 0 ? ['-'] : []), ...(onlyIntegers ? ['.'] : [])];
        if (!event.metaKey && blockKeys.includes(event.key)) {
            event.preventDefault();
        }
    };

    const formControlContent = (
        <FormControl
            aria-label={otherProps['aria-label']}
            aria-labelledby={otherProps['aria-labelledby']}
            id={name}
            value={internalValue ?? ''}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            className={className}
            style={style}
            type="number"
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
        />
    );

    if (prefixAddon) {
        return (
            <InputGroup>
                <InputGroup.Prepend>
                    <InputGroup.Text>{prefixAddon}</InputGroup.Text>
                </InputGroup.Prepend>
                {formControlContent}
            </InputGroup>
        );
    }
    return formControlContent;
};

NumberInput.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.shape({}),

    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['any'])]),
    onlyIntegers: PropTypes.bool,
    prefixAddon: PropTypes.node,
    debounce: PropTypes.bool,
};

NumberInput.defaultProps = {
    value: '',
    onBlur: null,
    className: '',
    style: null,

    placeholder: '',
    disabled: false,
    min: null,
    max: null,
    step: 'any',
    onlyIntegers: false,
    prefixAddon: null,
    debounce: false,
};

export const NumberField = asFormField({
    WrappedComponent: React.memo(NumberInput),
    onValidateField: (value, props) => {
        if (props.min && value !== null && value !== '' && value < props.min) {
            return `Enter a value of ${props.min} or more`;
        }
        if (props.max && value !== null && value !== '' && value > props.max) {
            return `Enter a value of ${props.max} or less`;
        }
    },
});
